import { dayjs, type Locale, validationMessages } from '@tnn/core'
import { domainLocales } from '@tnn/domain'
import i18next, { type i18n as I18n, type Resource } from 'i18next'
import { initReactI18next } from 'react-i18next'
import { z } from 'zod'
import { en, th } from './common'

export const locales: readonly Locale[] = ['th', 'en']
const common = { th, en }

export interface CreateI18nOptions {
  /** Extra namespaces an app wants to add, e.g. `{ th: { portal: {...} }, en: {...} }` */
  extra?: Partial<Record<Locale, Record<string, object>>>
  /** Starting language, from the user's saved preferences. Thai when not set. */
  lng?: Locale
}

/**
 * One i18next instance per app. Namespaces: `common` (shared) plus one per
 * module from `@tnn/domain` (`tms`, `accounting`, `admin`). Changing the
 * language also switches dayjs and Zod validation messages.
 */
export function createI18n({ extra, lng = 'th' }: CreateI18nOptions = {}): I18n {
  const resources: Resource = {}
  for (const locale of locales) {
    resources[locale] = { common: common[locale], ...domainLocales[locale], ...(extra?.[locale] ?? {}) }
  }

  const instance = i18next.createInstance()
  instance.use(initReactI18next).init({
    resources,
    lng,
    supportedLngs: locales,
    fallbackLng: 'th',
    load: 'languageOnly',
    defaultNS: 'common',
    ns: Object.keys(resources.th ?? {}),
    interpolation: { escapeValue: false },
    initAsync: false,
    react: { useSuspense: false },
  })

  const applyLocale = (language: string) => {
    const locale: Locale = language.startsWith('en') ? 'en' : 'th'
    dayjs.locale(locale)
    z.config(locale === 'th' ? z.locales.th() : z.locales.en())
    z.config({ customError: validationMessages(locale) })
    if (typeof document !== 'undefined') document.documentElement.lang = locale
  }
  applyLocale(instance.language ?? 'th')
  instance.on('languageChanged', applyLocale)
  return instance
}

/** Current language as a supported locale. */
export function currentLocale(i18n: I18n): Locale {
  return i18n.language?.startsWith('en') ? 'en' : 'th'
}

export { Trans, useTranslation } from 'react-i18next'
export { common }
